/**
 * OsintFeed — Right-rail signal feed for the globe takeover (S3.2).
 *
 * Renders a pre-filtered signal list per design ref
 * `design_handoff_echelon_2026_04_26/workspace/globeTakeover.jsx:180-248`.
 * Each row shows {id, ts, title, domain, severity, region} per Q-S3.0-c
 * resolution.
 *
 * Caller (GlobeTakeover) is responsible for fetching via `useOsintSignals`
 * + `useWorldMonitorLive` and applying LayerPanel filtering before passing
 * the `signals` prop here.
 */

import { cn } from '@/lib/utils';
import type { WorldMonitorSignal } from '../../api/worldMonitor';

interface OsintFeedProps {
  /** Pre-filtered signal corpus to render. */
  signals: WorldMonitorSignal[];
  onSelect: (signal: WorldMonitorSignal) => void;
  onClose: () => void;
  /** Optional className passthrough for positioning by the parent. */
  className?: string;
}

// Domain → token hue (mirrors GlobeCanvas DOMAIN_COLOR, kept local for
// independence from globe-internal constants).
const DOMAIN_COLOR: Record<string, string> = {
  geopolitical: 'var(--sig-iris)',
  finance: 'var(--sig-ember)',
  corporate: 'var(--sig-ember)',
  legal: 'var(--sig-ember)',
  academic: 'var(--sig-ember)',
  maritime: 'var(--sig-azure)',
  aviation: 'var(--sig-azure)',
  seismic: 'var(--sig-amber)',
  environmental: 'var(--sig-mint)',
  health: 'var(--sig-mint)',
  cyber: 'var(--sig-rose)',
  nuclear: 'var(--sig-amber)',
  sports: 'var(--sig-mint)',
  other: 'var(--e-text-muted)',
};

const SEV_COLOR: Record<string, string> = {
  critical: 'var(--sig-rose)',
  high: 'var(--sig-ember)',
  moderate: 'var(--sig-amber)',
  low: 'var(--sig-mint)',
};

function formatTimeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function OsintFeed({ signals, onSelect, onClose, className }: OsintFeedProps) {
  return (
    <div
      data-testid="osint-feed"
      className={cn(
        'w-80 flex flex-col rounded-lg border border-[var(--e-border-secondary)]',
        'bg-[var(--e-bg-card)]/95 backdrop-blur-md shadow-lg overflow-hidden',
        className,
      )}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--e-border-secondary)]/50">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wider uppercase text-[var(--e-text-muted)]">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--sig-mint)] animate-pulse" />
          OSINT · {signals.length} live
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close OSINT feed"
          className="text-[12px] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] px-2 py-0.5 border border-[var(--e-border-secondary)] rounded"
        >
          ×
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {signals.length === 0 ? (
          <div className="px-3 py-6 text-center text-[11px] text-[var(--e-text-muted)] italic">
            No signals match the current layer selection.
          </div>
        ) : (
          signals.map((s) => {
            const domainColor = DOMAIN_COLOR[s.domain] ?? 'var(--e-text-muted)';
            const sevColor = SEV_COLOR[s.severity] ?? 'var(--e-text-muted)';
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelect(s)}
                data-testid={`osint-feed-row-${s.id}`}
                className="w-full text-left px-3 py-2.5 border-b border-[var(--e-border-secondary)]/40 hover:bg-[var(--e-bg-hover)] transition-colors"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="text-[9px] font-mono text-[var(--e-text-muted)] uppercase tracking-wider">
                    {s.id}
                  </span>
                  <span className="text-[9px] font-mono text-[var(--e-text-muted)]">
                    {formatTimeAgo(s.timestamp)}
                  </span>
                </div>
                <div className="text-[12px] text-[var(--e-text-primary)] leading-snug mb-1.5">
                  {s.title}
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
                    style={{ color: domainColor, borderColor: domainColor }}
                  >
                    {s.domain}
                  </span>
                  <span
                    className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
                    style={{ color: sevColor, borderColor: sevColor }}
                  >
                    {s.severity}
                  </span>
                  {s.region && (
                    <span className="text-[9px] font-mono text-[var(--e-text-muted)] ml-auto">
                      {s.region}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
